import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, X } from "lucide-react";

/**
 * Published acceptance (or offer) rate for the row, shown as a sourced fact with its scope and cycle.
 * The backend's rule-based estimate (base rate × grade-gap multiplier) is deliberately NOT displayed:
 * it is uncalibrated and must not read as a personal chance. The Grade match band carries the fit result.
 */
const SCOPE = { university_all_courses: "university-wide, all courses" };
const KIND = { acceptance_rate: "acceptance rate", admit_rate: "admit rate" };
const n = (v) => (typeof v === "number" ? v.toLocaleString("en-GB") : null);
const describe = (r) => ({
  ...r,
  kind: KIND[r.measure] || "admission rate",
  scope: SCOPE[r.scope] || r.scope,
});
export const publishedRateLabel = (r) => `Published ${KIND[r?.measure] || "admission rate"}`;

export function AdmitProbabilityChip({ admitProbability, rowId }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); triggerRef.current?.focus(); };
  }, [open]);
  const pub = admitProbability?.published_rate;
  if (!pub) return null;
  const m = describe(pub);
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid={`admit-probability-chip-${rowId}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 rounded-md border border-stone-300 bg-white px-2 py-1 text-sm font-mono text-stone-800 whitespace-nowrap hover:bg-stone-50 transition-colors"
        title={`Published ${m.kind}: ${m.scope}, ${m.cycle}. Source: ${m.source}. Not your personal chance.`}
      >
        {m.value_pct}%
        <Info className="h-3.5 w-3.5 opacity-60" aria-hidden />
      </button>
      {open && createPortal(
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setOpen(false)} data-testid={`admit-probability-modal-${rowId}`}>
          <div className="bg-white rounded-lg shadow-xl border border-stone-200 max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="label-uppercase text-[10px] text-stone-500 mb-1">Published {m.kind}</div>
                <div className="font-editorial text-2xl leading-tight" data-testid={`admit-probability-rate-${rowId}`}>{m.value_pct}% · {m.scope}</div>
                <div className="text-[12px] text-stone-600 mt-1" data-testid={`admit-probability-source-${rowId}`}>
                  <a href={m.source_url} target="_blank" rel="noopener noreferrer" className="underline">{m.source}</a> · {m.cycle}
                </div>
                <div className="text-[12px] text-stone-500 mt-0.5" data-testid={`admit-probability-measure-${rowId}`}>Measure: {m.measure_label} · {m.population}</div>
                {n(m.counts?.numerator) && n(m.counts?.denominator) && (
                  <div className="text-[12px] text-stone-500 mt-0.5" data-testid={`admit-probability-counts-${rowId}`}>
                    Published counts: {n(m.counts.numerator)} {m.counts.numerator_label} of {n(m.counts.denominator)} {m.counts.denominator_label}. {m.ratio_note}.
                  </div>
                )}
              </div>
              <button ref={closeRef} type="button" aria-label="Close" onClick={() => setOpen(false)} data-testid={`admit-probability-close-${rowId}`} className="text-stone-500 hover:text-stone-800"><X className="h-5 w-5" /></button>
            </div>
            <div className="text-[13px] text-stone-700 space-y-3 leading-relaxed">
              <p>This is a published figure for past applicants as a group, across the whole university. It is not this course's rate and does not predict your own outcome.</p>
              <p><strong>Grade match</strong> is a separate, rule-based academic fit: it compares your grades, subjects, tests and English with the entry requirements on file. It has not been checked against real admission outcomes, so we don't turn it into a percentage.</p>
              <p className="text-[12px] text-stone-500">Other rates you may see, such as offer rates or modelled estimates, use different counts, cycles or populations, so they can differ from this figure and are not directly comparable with it.</p>
            </div>
            <div className="mt-5 flex items-center justify-between">
              <a href="/methodology#chance-score" data-testid={`admit-probability-methodology-${rowId}`} className="text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline">Read the full methodology →</a>
              <button type="button" onClick={() => setOpen(false)} data-testid={`admit-probability-dismiss-${rowId}`} className="text-[11px] font-underground-sc tracking-wider text-stone-500 hover:text-stone-800">Close</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

export default AdmitProbabilityChip;
